import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";
import {
  AlertDialog,
  AlertDialogFooter,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogAction,
  AlertDialogCancel,
} from "~/atoms/alert-dialog";

interface Props {
  hasUnsavedChanges: boolean;
  manualOpen?: boolean;
  onManualOpenChange?: (open: boolean) => void;
  onManualLeave?: () => void;
  onLeave?: () => void;
  ignoreSearchChanges?: boolean;
  title?: string;
  description?: string;
  cancelLabel?: string;
  confirmLabel?: string;
}

export const UnsavedChangesAlert = ({
  hasUnsavedChanges,
  manualOpen = false,
  onManualOpenChange,
  onManualLeave,
  onLeave,
  ignoreSearchChanges = false,
  title,
  description,
  cancelLabel,
  confirmLabel,
}: Props) => {
  const { t } = useTranslation();

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedChanges &&
      (currentLocation.pathname !== nextLocation.pathname ||
        (!ignoreSearchChanges &&
          currentLocation.search !== nextLocation.search) ||
        currentLocation.hash !== nextLocation.hash),
  );

  const isNavigationBlocked = blocker.state === "blocked";
  const isOpen = isNavigationBlocked || manualOpen;
  const resolvedTitle = title ?? t(($) => $.commons.unsavedChanges.title);
  const resolvedDescription =
    description ?? t(($) => $.commons.unsavedChanges.description);
  const resolvedCancelLabel =
    cancelLabel ?? t(($) => $.commons.unsavedChanges.continueEditing);
  const resolvedConfirmLabel =
    confirmLabel ?? t(($) => $.commons.unsavedChanges.discardChanges);

  const handleCancel = () => {
    if (isNavigationBlocked) {
      blocker.reset();
    }

    if (manualOpen) {
      onManualOpenChange?.(false);
    }
  };

  const handleLeave = () => {
    onLeave?.();

    if (isNavigationBlocked) {
      blocker.proceed();
    }

    if (manualOpen) {
      onManualOpenChange?.(false);
      onManualLeave?.();
    }
  };

  return (
    <AlertDialog open={isOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{resolvedTitle}</AlertDialogTitle>
          <AlertDialogDescription>{resolvedDescription}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel}>
            {resolvedCancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction variant="destructive" onClick={handleLeave}>
            {resolvedConfirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
